import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Link,
  Modal,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { CustomCheckBox } from "../../v2/utils/CustomCheckBox";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import { generateGtmEvent } from "../../../Helpers";

const useStyles = makeStyles((theme) => ({
  filterModal: {
    position: "absolute",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  modalTitle: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "20px",
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
}));

export interface FilterOptionProps {
  id: string;
  name: string;
}

interface FilterModalProps {
  open: boolean;
  handleClose: () => void;
  availableFilters: FilterOptionProps[];
  appliedFilters: FilterOptionProps[];
  onFilter: (filters: FilterOptionProps[]) => void;
  title: string;
  heading: string;
}

export const FilterModal = (props: FilterModalProps) => {
  const {
    open,
    handleClose,
    title,
    heading,
    availableFilters,
    appliedFilters,
    onFilter,
  } = props;
  const classes = useStyles();
  const horiBreakpointSm = useMediaQuery("(max-width:769px)");
  const [selectedFilters, setSelectedFilters] = useState<FilterOptionProps[]>([]);
  const applyFilter = () => {
    console.log("dataLayerObject", selectedFilters);
    onFilter(selectedFilters);
    handleClose();
  };

  useEffect(() => {
    setSelectedFilters(appliedFilters);
  }, [appliedFilters]);

  return (
    <>
      <Modal open={open} onClose={handleClose} aria-labelledby={"filter-modal"}>
        <Card
          sx={{ width: horiBreakpointSm ? "600px" : "900px" }}
          className={classes.filterModal}>
          <CardHeader
            sx={{ padding: "16px 24px" }}
            title={
              <Typography
                id="course-filter-modal-title"
                variant="h6"
                className={classes.modalTitle}>
                {title}
              </Typography>
            }
            action={
              <Tooltip
                placement="bottom-end"
                title="Close"
                arrow
                role="tooltip"
                describeChild>
                <IconButton
                  onClick={() => {
                    generateGtmEvent({
                      event: "modal",
                      action: "close",
                      name: "onclick",
                      type: "click",
                      region: "main content",
                      section: "filter",
                      text: "close cross",
                    });
                    props.handleClose();
                  }}
                  aria-label="close overall progress filter modal"
                  className={classes.closeButton}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <Divider />
          <CardContent
            sx={{
              padding: "24px 32px",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}>
            <Typography
              component={"p"}
              variant="h6"
              sx={{ color: "#000000", fontWeight: "bold", fontSize: "24px" }}>
              {heading}
            </Typography>
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)" }}>
                {availableFilters &&
                  availableFilters.length > 0 &&
                  availableFilters?.map((availableFilter, index) => {
                    return (
                      <CustomCheckBox
                        key={`option-${availableFilter.id}-${index}`}
                        label={availableFilter.name!}
                        checked={
                          selectedFilters.some((el) => el.id === availableFilter.id) ??
                          false
                        }
                        emitDataLayer={(checked) => {
                          generateGtmEvent({
                            event: "select",
                            action: checked ? "click" : "unclick",
                            name: "onclick",
                            type: "checkbox",
                            region: "main content",
                            section: heading,
                            text: `${availableFilter.name!}`,
                            component: "filter",
                          });
                        }}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          const newSelectedFilters = event.target.checked
                            ? [...selectedFilters, availableFilter]
                            : selectedFilters.filter(
                                (filter) => filter.id !== availableFilter.id
                              );
                          setSelectedFilters(newSelectedFilters);
                        }}
                      />
                    );
                  })}
              </Box>
            </>
          </CardContent>
          <Divider />
          <CardActions sx={{ padding: "24px 48px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}>
              <Link
                onClick={() => {
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "main content",
                    section: "filter",
                    text: "clear all",
                  });
                  onFilter([]);
                  handleClose();
                }}
                variant="body1">
                Clear all
              </Link>
              <Link
                onClick={() => {
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "main content",
                    section: "filter",
                    text: "show results",
                  });
                  applyFilter();
                }}
                underline="none">
                <Button
                  size="large"
                  variant="contained"
                  sx={{
                    borderRadius: "16px",
                    width: "180px",
                    height: "62px",
                    padding: "0px",
                    "&:hover": {
                      transform: "none",
                    },
                  }}>
                  Show Results
                </Button>
              </Link>
            </Box>
          </CardActions>
        </Card>
      </Modal>
    </>
  );
};
